import clsx from "clsx";
import React from "react";
import { FormattedMessage, type MessageDescriptor } from "react-intl";
import { Field } from "redux-form";

import { isChildrenCourse, isOneArchIncludedInCourse, isPowerCapCourse, isRetainersCourse } from "~/common/courses";
import { Arch, getOcclusalPlaneTeeth, LowerArch, OcclusalPlane, OcclusalPlaneAction, TeethLevelAlign, UpperArch } from "~/common/prescription";
import { Label } from "~/components/ui/label";
import { LabelledRadioGroup } from "~/components/ui/labelled-radio";
import type { ValueOf } from "~/types/common";
import type { TPatient } from "~/types/patient";
import type { TPrescriptionReduxForm, TPrescriptionReduxFormChangeFn } from "~/types/redux-form";

import occlusalPlaneLeftImgSrc from "./occlusal-plane-left.svg";
import occlusalPlaneParallelImgSrc from "./occlusal-plane-parallel.svg";
import occlusalPlaneRightImgSrc from "./occlusal-plane-right.svg";
import { RecipeCheckbox, RecipeRadio } from "./recipe-shared";

type RecipeTreatArchesProps = {
  required?: boolean;
  course_id: TPatient["course"]["course_id"] | null | undefined;
  upper_arch_action: ValueOf<typeof UpperArch> | null | undefined;
  lower_arch_action: ValueOf<typeof LowerArch> | null | undefined;
  occlusal_plane: TPrescriptionReduxForm["occlusal_plane"] | undefined;
  occlusal_plane_action: TPrescriptionReduxForm["occlusal_plane_action"] | undefined;
  resetForm(...args: string[]): void;
  change: TPrescriptionReduxFormChangeFn;
};

export function RecipeTreatArches({ required = true, course_id, upper_arch_action, lower_arch_action, occlusal_plane, occlusal_plane_action, resetForm, change }: RecipeTreatArchesProps) {
  return (
    <div id="treat-arches-section">
      {isRetainersCourse(course_id) ? null : (
        <h4 id="arches-heading" className="block">
          <FormattedMessage id="HEADER_ARCHES" />
        </h4>
      )}

      <ArchesOptions course_id={course_id} required={required} />

      {isRetainersCourse(course_id) || isPowerCapCourse(course_id) ? null : (
        <ArchesActionsOptions course_id={course_id} upper_arch_action={upper_arch_action} lower_arch_action={lower_arch_action} resetForm={resetForm} />
      )}

      {isChildrenCourse(course_id) || isRetainersCourse(course_id) || isPowerCapCourse(course_id) ? null : <ArchesTeethLevelAlignOptions />}

      {isRetainersCourse(course_id) || isPowerCapCourse(course_id) ? null : (
        <OcclusalPlaneOptions required={required} occlusal_plane={occlusal_plane} occlusal_plane_action={occlusal_plane_action} change={change} />
      )}
    </div>
  );
}

function ArchesOptions({ course_id, required }: Pick<RecipeTreatArchesProps, "course_id" | "required">) {
  return (
    <LabelledRadioGroup
      className="form-group"
      label={
        <>
          <FormattedMessage id={isPowerCapCourse(course_id) ? "pat.power_cap.arches_select" : "ARCHES_SELECT"} />
          {required ? (
            <span className="tw-text-orange-500" aria-hidden="true">
              &nbsp;*
            </span>
          ) : null}
        </>
      }
      labelProps={{ id: "validation-arch", size: "xl" }}
    >
      {isOneArchIncludedInCourse(course_id) ? null : <RecipeRadio name="arch" value={Arch.BOTH} intlId="TA_BOTH" />}

      <RecipeRadio name="arch" value={Arch.UPPER} intlId="TA_UPPER" />

      <RecipeRadio name="arch" value={Arch.LOWER} intlId="TA_LOWER" />
    </LabelledRadioGroup>
  );
}

function ArchesTeethLevelAlignOptions() {
  return (
    <div className="row">
      <div className="form-group col-md-4 tw-mb-5">
        <Label id="teeth-level-upper-label" readOnly>
          <FormattedMessage id="ARCHES_TLA_UPPER" />
        </Label>

        <div className="checkbox-list checkbox_list">
          <RecipeCheckbox name="teeth_level_align" value={TeethLevelAlign.MARGIN} intlId="TLA_G_MARGIN" ariaLabelledBy="teeth-level-upper-label" />

          <RecipeCheckbox name="teeth_level_align" value={TeethLevelAlign.EDGE} intlId="TLA_C_EDGE" ariaLabelledBy="teeth-level-upper-label" />
        </div>
      </div>

      <div className="form-group col-md-4 tw-mb-5">
        <Label id="teeth-level-lower-label" readOnly>
          <FormattedMessage id="ARCHES_TLA_LOWER" />
        </Label>

        <div className="checkbox-list checkbox_list">
          <RecipeCheckbox name="teeth_level_align_lower" value={TeethLevelAlign.MARGIN} intlId="TLA_G_MARGIN" ariaLabelledBy="teeth-level-lower-label" />

          <RecipeCheckbox name="teeth_level_align_lower" value={TeethLevelAlign.EDGE} intlId="TLA_C_EDGE" ariaLabelledBy="teeth-level-lower-label" />
        </div>
      </div>
    </div>
  );
}

function ArchesActionsOptions({ course_id, upper_arch_action, lower_arch_action, resetForm }: Pick<RecipeTreatArchesProps, "course_id" | "upper_arch_action" | "lower_arch_action" | "resetForm">) {
  return (
    <div className="row">
      <div className="form-group col-md-4" style={{ marginBottom: 20 }}>
        <Label id="validation-upper_arch_action" readOnly>
          <FormattedMessage id="ARCHES_U_SELECT" />
        </Label>

        <div className="checkbox-list checkbox_list">
          <RecipeCheckbox
            name="upper_arch_action"
            value={UpperArch.MAINTAIN}
            intlId="TA_U_MAINTAIN"
            ariaLabelledBy="validation-upper_arch_action"
            onChange={() => resetForm("upper_arch_expansion_torque", "upper_arch_expansion_bodily")}
          />

          <RecipeCheckbox
            name="upper_arch_action"
            value={UpperArch.EXPAND}
            intlId="TA_U_EXPAND"
            ariaLabelledBy="validation-upper_arch_action"
            onChange={() => resetForm("upper_arch_expansion_torque", "upper_arch_expansion_bodily")}
          />

          {upper_arch_action == UpperArch.EXPAND ? renderExpansionArchOptions("upper") : false}

          {isChildrenCourse(course_id) ? null : (
            <RecipeCheckbox
              name="upper_arch_action"
              value={UpperArch.NARROW}
              intlId="TA_U_NARROW"
              ariaLabelledBy="validation-upper_arch_action"
              onChange={() => resetForm("upper_arch_expansion_torque", "upper_arch_expansion_bodily")}
            />
          )}
        </div>
      </div>

      <div className="form-group col-md-4" style={{ marginBottom: 20 }}>
        <Label id="validation-lower_arch_action" readOnly>
          <FormattedMessage id="ARCHES_L_SELECT" />
        </Label>

        <div className="checkbox-list checkbox_list">
          <RecipeCheckbox
            name="lower_arch_action"
            value={LowerArch.MAINTAIN}
            intlId="TA_L_MAINTAIN"
            ariaLabelledBy="validation-lower_arch_action"
            onChange={() => resetForm("lower_arch_expansion_torque", "lower_arch_expansion_bodily")}
          />

          <RecipeCheckbox
            name="lower_arch_action"
            value={LowerArch.EXPAND}
            intlId="TA_L_EXPAND"
            ariaLabelledBy="validation-lower_arch_action"
            onChange={() => resetForm("lower_arch_expansion_torque", "lower_arch_expansion_bodily")}
          />

          {lower_arch_action == LowerArch.EXPAND ? renderExpansionArchOptions("lower") : false}

          {isChildrenCourse(course_id) ? null : (
            <RecipeCheckbox
              name="lower_arch_action"
              value={LowerArch.NARROW}
              intlId="TA_L_NARROW"
              ariaLabelledBy="validation-lower_arch_action"
              onChange={() => resetForm("lower_arch_expansion_torque", "lower_arch_expansion_bodily")}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function renderExpansionArchOptions(arch: "upper" | "lower") {
  return (
    <div className="row">
      <div className="form-group col-md-offset-2 col-sm-offset-1 col-xs-offset-1 rd-arch-expand-options">
        <Label readOnly>
          <FormattedMessage id="TA_U_EXPAND_TYPE_HOWTO" />
        </Label>

        <div className="tw-flex tw-flex-col tw-gap-1">
          <label className="tw-inline-flex tw-items-center tw-gap-1.5" htmlFor={`${arch}_arch_expansion_torque`}>
            <Field
              id={`${arch}_arch_expansion_torque`}
              aria-labelledby={clsx(`validation-${arch}_arch_action`, `${arch}_arch_expansion_torque`)}
              className="rd-arch-expansion-checkbox tw-m-0"
              type="checkbox"
              name={`${arch}_arch_expansion_torque`}
              component="input"
            />

            <FormattedMessage id="TA_U_EXPAND_TYPE_TORQUE" />
          </label>

          <label className="tw-inline-flex tw-items-center tw-gap-1.5" htmlFor={`${arch}_arch_expansion_bodily`}>
            <Field
              aria-labelledby={clsx(`validation-${arch}_arch_action`, `${arch}_arch_expansion_bodily`)}
              id={`${arch}_arch_expansion_bodily`}
              className="rd-arch-expansion-checkbox tw-m-0"
              type="checkbox"
              component="input"
              name={`${arch}_arch_expansion_bodily`}
            />

            <FormattedMessage id="TA_U_EXPAND_TYPE_BODILY" />
          </label>
        </div>
      </div>
    </div>
  );
}

function OcclusalPlaneOptions({ required, occlusal_plane, occlusal_plane_action, change }: Pick<RecipeTreatArchesProps, "required" | "occlusal_plane" | "occlusal_plane_action" | "change">) {
  function handleChange() {
    change("occlusal_plane_action", null);
    handleActionChange();
  }

  function handleActionChange() {
    change("occlusal_plane_subaction", null);
    handleSubActionChange();
  }

  function handleSubActionChange() {
    if (occlusal_plane == null || occlusal_plane == OcclusalPlane.PARALLEL) return;

    for (const tooth of getOcclusalPlaneTeeth(occlusal_plane, OcclusalPlaneAction.EXTRUDE)) {
      change(`occlusal_plane_extrude_${tooth}`, false);
    }
    for (const tooth of getOcclusalPlaneTeeth(occlusal_plane, OcclusalPlaneAction.INTRUDE)) {
      change(`occlusal_plane_intrude_${tooth}`, false);
    }
  }

  function handleToothChange(tooth: number, checked: boolean) {
    if (!checked) return;
    if (occlusal_plane == null || occlusal_plane == OcclusalPlane.PARALLEL) return;

    if (getOcclusalPlaneTeeth(occlusal_plane, OcclusalPlaneAction.EXTRUDE).includes(tooth)) {
      change("occlusal_plane_subaction", OcclusalPlaneAction.EXTRUDE);

      for (const tooth of getOcclusalPlaneTeeth(occlusal_plane, OcclusalPlaneAction.INTRUDE)) {
        change(`occlusal_plane_intrude_${tooth}`, false);
      }
    } else {
      change("occlusal_plane_subaction", OcclusalPlaneAction.INTRUDE);

      for (const tooth of getOcclusalPlaneTeeth(occlusal_plane, OcclusalPlaneAction.EXTRUDE)) {
        change(`occlusal_plane_extrude_${tooth}`, false);
      }
    }
  }

  return (
    <div className="form-group">
      <Label id="validation-occlusal_plane" readOnly>
        <FormattedMessage id="TA_OCCLUSAL" />
        {required ? (
          <span className="tw-text-orange-500" aria-hidden="true">
            &nbsp;*
          </span>
        ) : null}
      </Label>

      <div className="tw-flex tw-flex-wrap tw-gap-4">
        <OcclusalPlaneCard value={OcclusalPlane.PARALLEL} intlId="TA_P_OCCLUSAL" imgSrc={occlusalPlaneParallelImgSrc} selected={occlusal_plane == OcclusalPlane.PARALLEL} onChange={handleChange} />

        <OcclusalPlaneCard value={OcclusalPlane.RIGHT} intlId="TA_R_OCCLUSAL" imgSrc={occlusalPlaneRightImgSrc} selected={occlusal_plane == OcclusalPlane.RIGHT} onChange={handleChange}>
          {occlusal_plane == OcclusalPlane.RIGHT ? (
            <OcclusalPlaneActions
              occlusal_plane={occlusal_plane}
              occlusal_plane_action={occlusal_plane_action}
              onActionChange={handleActionChange}
              onSubActionChange={handleSubActionChange}
              onToothChange={handleToothChange}
              ariaLabelledBy={`occlusal_plane-value-${OcclusalPlane.RIGHT}`}
            />
          ) : null}
        </OcclusalPlaneCard>

        <OcclusalPlaneCard value={OcclusalPlane.LEFT} intlId="TA_L_OCCLUSAL" imgSrc={occlusalPlaneLeftImgSrc} selected={occlusal_plane == OcclusalPlane.LEFT} onChange={handleChange}>
          {occlusal_plane == OcclusalPlane.LEFT ? (
            <OcclusalPlaneActions
              occlusal_plane={occlusal_plane}
              occlusal_plane_action={occlusal_plane_action}
              onActionChange={handleActionChange}
              onSubActionChange={handleSubActionChange}
              onToothChange={handleToothChange}
              ariaLabelledBy={`occlusal_plane-value-${OcclusalPlane.LEFT}`}
            />
          ) : null}
        </OcclusalPlaneCard>
      </div>
    </div>
  );
}

function OcclusalPlaneCard({
  value,
  intlId,
  imgSrc,
  selected,
  onChange,
  children,
}: React.PropsWithChildren<{
  value: ValueOf<typeof OcclusalPlane>;
  intlId: MessageDescriptor["id"];
  imgSrc: string;
  selected: boolean;
  onChange(): void;
}>) {
  const inputId = `occlusal_plane-value-${value}`;

  return (
    <div className="tw-flex tw-w-full tw-max-w-full tw-flex-col tw-gap-3 desktop:tw-w-[240px]">
      <label
        htmlFor={inputId}
        className={clsx(
          "tw-relative tw-flex tw-h-[160px] tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-solid tw-p-6",
          selected ? "tw-border-foreground tw-bg-muted" : "tw-border-border tw-bg-background",
        )}
      >
        <span className="rd-radio tw-absolute tw-left-4 tw-top-4">
          <Field
            id={inputId}
            aria-labelledby={clsx("validation-occlusal_plane", `${inputId}-label`)}
            name="occlusal_plane"
            component="input"
            type="checkbox"
            className="rd-radio__input"
            format={(v?: number) => v === value}
            normalize={(v: boolean) => (v ? value : null)}
            onChange={onChange}
          />
          <span className="rd-radio__face" aria-hidden="true">
            <span className="rd-radio__dot" />
          </span>
        </span>

        <img src={imgSrc} alt="" width={103} height={103} className="tw-size-[103px]" />
      </label>

      <div className="tw-flex tw-flex-col tw-gap-1">
        <span id={`${inputId}-label`} className="tw-font-sans tw-text-sm tw-font-semibold tw-leading-5 tw-tracking-normal tw-text-foreground">
          <FormattedMessage id={intlId} /> (до лечения)
        </span>

        {children}
      </div>
    </div>
  );
}

function OcclusalPlaneActions({
  occlusal_plane,
  occlusal_plane_action,
  onActionChange,
  onSubActionChange,
  onToothChange,
  ariaLabelledBy,
}: {
  occlusal_plane: (typeof OcclusalPlane)["RIGHT"] | (typeof OcclusalPlane)["LEFT"];
  occlusal_plane_action: TPrescriptionReduxForm["occlusal_plane_action"] | undefined;
  onActionChange(): void;
  onSubActionChange(): void;
  onToothChange(tooth: number, checked: boolean): void;
  ariaLabelledBy: string;
}) {
  return (
    <div className="checkbox-list checkbox_list">
      <RecipeCheckbox name="occlusal_plane_action" value={OcclusalPlaneAction.SAVE} intlId="TA_OCCLUSAL_SAVE" onChange={onActionChange} ariaLabelledBy={ariaLabelledBy} />

      <RecipeCheckbox name="occlusal_plane_action" value={OcclusalPlaneAction.MODIFY} intlId="TA_OCCLUSAL_MODIFY" onChange={onActionChange} ariaLabelledBy={ariaLabelledBy} />

      {occlusal_plane_action == OcclusalPlaneAction.MODIFY ? (
        <div className="checkbox-list checkbox_list">
          <RecipeCheckbox name="occlusal_plane_subaction" value={OcclusalPlaneAction.EXTRUDE} intlId="TA_OCCLUSAL_TEETH_EXTRUDE" onChange={onSubActionChange} ariaLabelledBy={ariaLabelledBy} />

          <div className="tw-flex tw-items-center tw-gap-2 tw-pl-8">
            {getOcclusalPlaneTeeth(occlusal_plane, OcclusalPlaneAction.EXTRUDE).map((tooth) => (
              <OcclusalPlaneTooth key={tooth} tooth={tooth} occlusal_plane_subaction={OcclusalPlaneAction.EXTRUDE} onChange={(e) => onToothChange(tooth, e.target.checked)} />
            ))}
          </div>

          <RecipeCheckbox name="occlusal_plane_subaction" value={OcclusalPlaneAction.INTRUDE} intlId="TA_OCCLUSAL_TEETH_INTRUDE" onChange={onSubActionChange} ariaLabelledBy={ariaLabelledBy} />

          <div className="tw-flex tw-items-center tw-gap-2 tw-pl-8">
            {getOcclusalPlaneTeeth(occlusal_plane, OcclusalPlaneAction.INTRUDE).map((tooth) => (
              <OcclusalPlaneTooth key={tooth} tooth={tooth} occlusal_plane_subaction={OcclusalPlaneAction.INTRUDE} onChange={(e) => onToothChange(tooth, e.target.checked)} />
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

type TOcclusalPlaneSubAction = NonNullable<TPrescriptionReduxForm["occlusal_plane_subaction"]>;

const subActionToString = {
  [OcclusalPlaneAction.EXTRUDE]: "extrude",
  [OcclusalPlaneAction.INTRUDE]: "intrude",
} as const satisfies Record<TOcclusalPlaneSubAction, string>;

function OcclusalPlaneTooth({ tooth, occlusal_plane_subaction, onChange }: { tooth: number; occlusal_plane_subaction: TOcclusalPlaneSubAction; onChange: React.ChangeEventHandler<HTMLInputElement> }) {
  const action = subActionToString[occlusal_plane_subaction];
  const inputId = `occlusal-plane-${action}-${tooth}`;

  return (
    <label key={tooth} htmlFor={inputId} className="tw-flex tw-items-center tw-gap-2">
      <Field
        id={inputId}
        aria-labelledby={clsx(`occlusal_plane_subaction-value-${occlusal_plane_subaction}`, inputId)}
        className="tw-m-0"
        name={`occlusal_plane_${action}_${tooth}`}
        component="input"
        type="checkbox"
        onChange={onChange}
      />

      <span>{tooth}</span>
    </label>
  );
}
